import React, { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  getSummary, getTopPages, getDevices, getCountries, getTopReferrers, resetAnalytics,
} from "../../api/analyticsAPI";
import ThemeSwitcher from "./ThemeSwitcher";

const COLORS = ["#6366f1", "#22d3ee", "#a78bfa", "#34d399", "#fb923c", "#f472b6", "#facc15", "#60a5fa"];

const StatCard = ({ label, value, sub, color = "text-indigo-400", icon }) => (
  <div className="bg-zinc-900/40 border border-zinc-800 p-5 rounded-2xl flex items-start gap-4">
    {icon && (
      <div className="p-2 rounded-xl bg-zinc-800/80 text-zinc-400 shrink-0">
        {icon}
      </div>
    )}
    <div>
      <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{label}</p>
      <p className={`text-2xl font-black mt-1 ${color}`}>{value ?? "—"}</p>
      {sub && <p className="text-[10px] text-zinc-600 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const SectionTitle = ({ children }) => (
  <h3 className="text-xs font-black uppercase tracking-widest text-zinc-400 mb-4">{children}</h3>
);

const EmptyState = ({ height = 180 }) => (
  <div className={`flex items-center justify-center text-zinc-600 text-sm`} style={{ height }}>
    Belum ada data
  </div>
);

const LoadingState = ({ height = 180 }) => (
  <div className={`flex items-center justify-center text-zinc-600 text-sm`} style={{ height }}>
    <div className="flex gap-1">
      <span className="w-1.5 h-1.5 bg-zinc-600 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
      <span className="w-1.5 h-1.5 bg-zinc-600 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
      <span className="w-1.5 h-1.5 bg-zinc-600 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
    </div>
  </div>
);

const chartTooltipStyle = {
  contentStyle: { backgroundColor: "#18181b", border: "1px solid #3f3f46", borderRadius: 8, fontSize: 12 },
  labelStyle: { color: "#a1a1aa" },
  itemStyle: { color: "#e4e4e7" },
};

const DashboardC = () => {
  const [range, setRange] = useState("daily");
  const [summary, setSummary] = useState(null);
  const [topPages, setTopPages] = useState([]);
  const [devicesData, setDevicesData] = useState({ byDevice: [], byBrowser: [], byOs: [] });
  const [countries, setCountries] = useState([]);
  const [referrers, setReferrers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deviceTab, setDeviceTab] = useState("device"); // device | browser | os
  const [resetting, setResetting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, tp, dv, ct, ref] = await Promise.all([
        getSummary(range),
        getTopPages(),
        getDevices(),
        getCountries(),
        getTopReferrers(),
      ]);
      setSummary(s);
      setTopPages(Array.isArray(tp) ? tp : []);
      setDevicesData({
        byDevice: dv?.byDevice ?? [],
        byBrowser: dv?.byBrowser ?? [],
        byOs: dv?.byOs ?? [],
      });
      setCountries(Array.isArray(ct) ? ct : []);
      setReferrers(Array.isArray(ref) ? ref : []);
    } catch {
      setError("Failed to load analytics data. Make sure the backend is running.");
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const handleReset = async () => {
    const result = await Swal.fire({
      title: "Reset all analytics?",
      text: "This will permanently delete all visit data.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Yes, reset",
      cancelButtonText: "Cancel",
      background: "#18181b",
      color: "#fff",
    });
    if (!result.isConfirmed) return;
    setResetting(true);
    try {
      await resetAnalytics();
      await load();
    } finally {
      setResetting(false);
    }
  };

  const timeSeries = summary?.series ?? [];
  const totalVisits = summary?.totals?.total_visits ?? 0;
  const uniqueIps = summary?.totals?.total_unique ?? 0;

  // pick data for the device pie based on active tab
  const activePieData = {
    device: devicesData.byDevice,
    browser: devicesData.byBrowser,
    os: devicesData.byOs,
  }[deviceTab] ?? [];
  const activePieKey = { device: "device", browser: "browser", os: "os" }[deviceTab];

  // Bounce rate estimate: visits per unique (lower = better engagement, not really bounce rate)
  const avgPagesPerVisitor = uniqueIps > 0 ? (totalVisits / uniqueIps).toFixed(1) : "—";

  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100 p-6 lg:p-10">
      {/* Header */}
      <header className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-1 w-10 bg-indigo-500 rounded-full" />
          <span className="text-[10px] font-black uppercase tracking-[0.3em] text-indigo-400">Visitor Analytics</span>
        </div>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <h2 className="text-4xl font-black tracking-tight text-white">
            DASHBOARD <span className="text-indigo-500">ANALYTICS</span>
          </h2>
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-600 mr-1">Range:</span>
            {["daily", "weekly", "monthly"].map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={`px-4 py-1.5 rounded-lg text-xs font-bold uppercase tracking-widest transition-all ${
                  range === r ? "bg-indigo-600 text-white" : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                }`}
              >
                {r === "daily" ? "Daily" : r === "weekly" ? "Weekly" : "Monthly"}
              </button>
            ))}
            <button
              onClick={load}
              className="ml-2 p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white transition-all"
              title="Refresh"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <button
              onClick={handleReset}
              disabled={resetting}
              className={`ml-1 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-widest transition-all bg-zinc-800 text-red-400 hover:bg-red-900/40 hover:text-red-300 ${resetting ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {resetting ? "..." : "Reset"}
            </button>
          </div>
        </div>
      </header>

      {error && (
        <div className="mb-6 p-4 bg-red-900/30 border border-red-800 rounded-xl text-red-400 text-sm flex items-center gap-3">
          <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Total Visits"
          value={loading ? "..." : totalVisits.toLocaleString()}
          sub={`range: ${range}`}
          color="text-indigo-400"
          icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>}
        />
        <StatCard
          label="Unique Visitor"
          value={loading ? "..." : uniqueIps.toLocaleString()}
          sub="by IP hash"
          color="text-cyan-400"
          icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>}
        />
        <StatCard
          label="Pg/Visitor"
          value={loading ? "..." : avgPagesPerVisitor}
          sub="avg pages/visitor"
          color="text-violet-400"
          icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>}
        />
        <StatCard
          label="Top Country"
          value={loading ? "..." : (countries[0]?.country ?? "—")}
          sub={countries[0] ? `${countries[0].visits} visits` : "no data yet"}
          color="text-emerald-400"
          icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064"/></svg>}
        />
      </div>

      {/* Time Series Chart */}
      <div className="bg-zinc-900/40 border border-zinc-800 p-6 rounded-2xl mb-6">
        <div className="flex items-center justify-between mb-4">
          <SectionTitle>
            Visits per {range === "daily" ? "Day (last 30 days)" : range === "weekly" ? "Week (last 12 weeks)" : "Month (last 12 months)"}
          </SectionTitle>
        </div>
        {loading ? (
          <LoadingState height={240} />
        ) : timeSeries.length === 0 ? (
          <EmptyState height={240} />
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={timeSeries}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis
                dataKey="bucket"
                tick={{ fill: "#71717a", fontSize: 10 }}
                tickLine={false}
                axisLine={{ stroke: "#27272a" }}
              />
              <YAxis
                tick={{ fill: "#71717a", fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                width={35}
              />
              <Tooltip {...chartTooltipStyle} />
              <Legend
                wrapperStyle={{ fontSize: 11, paddingTop: 12 }}
                formatter={(val) => val === "visits" ? "Total" : "Unique"}
              />
              <Line type="monotone" dataKey="visits" name="visits" stroke="#6366f1" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
              <Line type="monotone" dataKey="unique_visitors" name="unique_visitors" stroke="#22d3ee" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* 3-column row: Devices + Top Pages + Referrers */}
      <div className="grid lg:grid-cols-3 gap-6 mb-6">

        {/* Devices / Browser / OS Pie */}
        <div className="bg-zinc-900/40 border border-zinc-800 p-6 rounded-2xl">
          <div className="flex items-center justify-between mb-4">
            <SectionTitle>Device Info</SectionTitle>
            <div className="flex gap-1 bg-zinc-800 rounded-lg p-0.5">
              {[["device", "Device"], ["browser", "Browser"], ["os", "OS"]].map(([val, lbl]) => (
                <button
                  key={val}
                  onClick={() => setDeviceTab(val)}
                  className={`px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${
                    deviceTab === val ? "bg-zinc-600 text-white" : "text-zinc-500 hover:text-zinc-300"
                  }`}
                >
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          {loading ? <LoadingState /> : activePieData.length === 0 ? <EmptyState /> : (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie
                    data={activePieData}
                    dataKey="visits"
                    nameKey={activePieKey}
                    cx="50%" cy="50%"
                    innerRadius={45}
                    outerRadius={75}
                  >
                    {activePieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip
                    {...chartTooltipStyle}
                    formatter={(val, name) => [val, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5 mt-2">
                {activePieData.slice(0, 5).map((d, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                      <span className="text-zinc-400 truncate max-w-[120px]">{d[activePieKey]}</span>
                    </div>
                    <span className="text-zinc-500">{d.visits}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Top Pages */}
        <div className="bg-zinc-900/40 border border-zinc-800 p-6 rounded-2xl">
          <SectionTitle>Top Pages</SectionTitle>
          {loading ? <LoadingState /> : topPages.length === 0 ? <EmptyState /> : (
            <div className="space-y-2.5">
              {topPages.slice(0, 8).map((p, i) => {
                const max = topPages[0]?.visits ?? 1;
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-zinc-400 truncate max-w-[160px]" title={p.path}>{p.path}</span>
                      <span className="text-xs text-zinc-500 ml-2 shrink-0">{p.visits}</span>
                    </div>
                    <div className="w-full bg-zinc-800 rounded-full h-1">
                      <div
                        className="h-1 rounded-full bg-indigo-500"
                        style={{ width: `${(p.visits / max) * 100}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top Referrers */}
        <div className="bg-zinc-900/40 border border-zinc-800 p-6 rounded-2xl">
          <SectionTitle>Top Referrers</SectionTitle>
          {loading ? <LoadingState /> : referrers.length === 0 ? <EmptyState /> : (
            <div className="space-y-2.5">
              {referrers.slice(0, 8).map((r, i) => {
                const max = referrers[0]?.visits ?? 1;
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-zinc-400 truncate max-w-[160px]" title={r.referrer}>{r.referrer}</span>
                      <span className="text-xs text-zinc-500 ml-2 shrink-0">{r.visits}</span>
                    </div>
                    <div className="w-full bg-zinc-800 rounded-full h-1">
                      <div
                        className="h-1 rounded-full bg-cyan-500"
                        style={{ width: `${(r.visits / max) * 100}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Theme Switcher */}
      <div className="mb-6">
        <ThemeSwitcher />
      </div>

      {/* Countries */}
      <div className="bg-zinc-900/40 border border-zinc-800 p-6 rounded-2xl">
        <SectionTitle>Visits by Country</SectionTitle>
        <p className="text-[10px] text-zinc-600 mb-3">
          Country detected from public IP. During local development (localhost/127.0.0.1), all entries will show as <span className="text-zinc-500">(unknown)</span> — this is normal and will resolve after deploying to a server.
        </p>
        {loading ? <LoadingState /> : countries.length === 0 ? <EmptyState height={100} /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2.5">
            {countries.slice(0, 16).map((c, i) => {
              const max = countries[0]?.visits ?? 1;
              return (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs text-zinc-400 w-28 truncate shrink-0">{c.country}</span>
                  <div className="flex-1 bg-zinc-800 rounded-full h-1.5">
                    <div
                      className="h-1.5 rounded-full"
                      style={{ width: `${(c.visits / max) * 100}%`, backgroundColor: COLORS[i % COLORS.length] }}
                    />
                  </div>
                  <span className="text-xs text-zinc-500 w-10 text-right shrink-0">{c.visits}</span>
                  <span className="text-[10px] text-zinc-700 w-12 text-right shrink-0">{c.unique_visitors} uniq</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardC;
